// @ts-check

// Shared imports
const {
  decodeBase64,
  stringToUtf8ByteArray
} = require('shared/string-utils')

const DATA_URI_REGEXP = /^data:([^,]+)?,(.*)$/
const DATA_URI_MEDIA_REGEXP = /^([^/]+\/[^;]+(;[^;]+)*?)(;base64)?$/
const DATA_URI_DEFAULT_MEDIA_TYPE = 'text/plain'

class DataURI {
  /**
   * @param {string} uri
   * @constructor
   * @public
   * @see https://en.wikipedia.org/wiki/Data_URI_scheme
   * @see https://tools.ietf.org/html/rfc2397
   */
  constructor(uri) {
    const uriMatch = DATA_URI_REGEXP.exec(uri)
    if (!uriMatch) {
      throw new URIError('uri argument must be a data URI')
    }
    this.uri = uri
    this.mediaType = DATA_URI_DEFAULT_MEDIA_TYPE
    this.base64 = false
    const mediaMatch = DATA_URI_MEDIA_REGEXP.exec(uriMatch[1])
    if (mediaMatch) {
      this.mediaType = mediaMatch[1]
      this.base64 = !!mediaMatch[3]
    }
    this.encodedData = uriMatch[2]
  }

  /**
   * Convenience helper to convert a data URI to a `Blob`
   * @param {string} uri
   * @returns {Blob}
   * @public
   */
  static toBlob(uri) {
    return (new DataURI(uri)).decodeAsBlob()
  }

  /**
   * @returns {string} The original data URI
   * @public
   */
  toString() {
    return this.uri
  }

  /**
   * Decode the data URI as a `String`.
   * @returns {string} Decoded data as a `String`
   * @public
   */
  decodeAsString() {
    if (this.base64) {
      return decodeBase64(this.encodedData)
    }
    return decodeURIComponent(this.encodedData)
  }

  /**
   * Decode the data URI as an `Array` of `Uint8` values.
   * @returns {Array.<number>} Decoded data as an `Array` of bytes
   * @public
   */
  decodeAsByteArray() {
    const decodedString = this.decodeAsString()
    if (this.base64) {
      const bytes = []
      for (let i = 0; i < decodedString.length; i++) {
        bytes.push(decodedString.charCodeAt(i))
      }
      return bytes
    }
    return stringToUtf8ByteArray(decodedString)
  }

  /**
   * Decode the data URI as an `ArrayBuffer` of `Uint8` values.
   * @returns {ArrayBuffer} Decoded data as an `ArrayBuffer`
   * @public
   */
  decodeAsArrayBuffer() {
    const bytes = this.decodeAsByteArray()
    const arrayBuffer = new ArrayBuffer(bytes.length)
    const dataView = new DataView(arrayBuffer)
    for (let i = 0; i < bytes.length; i++) {
      dataView.setUint8(i, bytes[i])
    }
    return arrayBuffer
  }

  /**
   * Decode the data URI as a `Blob` of `Uint8` values with a MIME type.
   * @returns {Blob} Decoded data as a `Blob`
   * @public
   */
  decodeAsBlob() {
    return new Blob([ this.decodeAsArrayBuffer() ], {
      type: this.mediaType
    })
  }
}

module.exports = {
  DataURI
}
